import React, { useState } from 'react';
import axios from 'axios';
import { useSettings } from '../contexts/SettingsContext';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Chip,
  TextField,
  MenuItem
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const LiveTradingButton = ({ tradeData, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tradingMode, setTradingMode] = useState('spot');
  const { settings } = useSettings();

  const isPremium = settings?.subscription?.subscriptionPlan === 'premium';

  const executeLiveTrade = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('idToken');
      const payload = {
        symbol: tradeData.symbol,
        side: tradeData.category === 'short' ? 'SELL' : 'BUY',
        type: 'LIMIT',
        tradingMode,
        quantity: tradeData.positionSize,
        price: tradeData.entryPrice
      };
      
      if (tradingMode === 'leverage') {
        payload.positionSide = tradeData.category === 'short' ? 'SHORT' : 'LONG';
        payload.leverage = tradeData.leverage || 1;
      }
      
      const response = await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/livetrading/execute', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (onSuccess) {
        onSuccess(response.data);
      }
      setOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to execute live trade');
    } finally {
      setLoading(false);
    }
  };

  if (!isPremium) {
    return (
      <Button
        variant="outlined"
        disabled
        startIcon={<RocketLaunchIcon />}
        sx={{ opacity: 0.5 }}
      >
        Live Trade (Premium)
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<RocketLaunchIcon />}
        onClick={() => setOpen(true)}
        sx={{ 
          background: 'linear-gradient(45deg, #FF6B6B 30%, #4ECDC4 90%)',
          color: 'white'
        }}
      >
        Execute Live Trade
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RocketLaunchIcon color="secondary" />
          Execute Live Trade on BingX
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <TextField 
            select 
            fullWidth 
            label="Trading Mode" 
            value={tradingMode} 
            onChange={(e) => setTradingMode(e.target.value)} 
            margin="normal"
            sx={{ mb: 2 }}
          >
            <MenuItem value="spot">Spot Trading</MenuItem>
            <MenuItem value="leverage">Leverage Trading</MenuItem>
          </TextField>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>Trade Details:</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip label={`Symbol: ${tradeData.symbol}`} />
              <Chip label={tradingMode === 'spot' ? 'SPOT' : `${tradeData.category === 'short' ? 'SHORT' : 'LONG'}`} color={tradingMode === 'leverage' ? 'warning' : 'default'} />
              <Chip 
                label={`${tradeData.category === 'short' ? 'SELL' : 'BUY'}`}
                color={tradeData.category === 'short' ? 'error' : 'success'}
              />
              <Chip label={`Size: ${tradeData.positionSize}`} />
              <Chip label={`Entry: $${tradeData.entryPrice}`} />
              {tradingMode === 'leverage' && <Chip label={`Leverage: ${tradeData.leverage || 1}x`} />}
            </Box>
          </Box>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This will execute a real trade on BingX using your configured API keys. 
              Make sure your trade details are correct before proceeding.
            </Typography>
          </Alert>

          <Alert severity="info">
            <Typography variant="body2">
              The trade will be executed at market price and tracked in your Live Trading dashboard.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={executeLiveTrade}
            variant="contained"
            color="secondary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <TrendingUpIcon />}
          >
            {loading ? 'Executing...' : 'Execute Trade'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LiveTradingButton;